import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiHome, FiFolder, FiMessageSquare, FiUser, FiCalendar } from 'react-icons/fi';
import { useAtom } from 'jotai';
import { activeTabAtom, TabType } from '../../atoms/v0/uiAtoms';
import { useEffect, startTransition, useCallback, useState } from 'react';
import { IconType } from 'react-icons';
import { useThemeMode } from '../../hooks/v0/useThemeMode';
import { useTranslation } from 'react-i18next';

// 自定义猫娘图标组件

interface NavItem {
  icon: IconType | React.FC;
  labelKey: string;
  path: string;
  tab: TabType;
}

export const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useAtom(activeTabAtom);
  const { isDarkMode } = useThemeMode();
  const { t } = useTranslation(['v0/common']);
  const [navTransitioning, setNavTransitioning] = useState(false);

  // 导航项目使用翻译键 - 更新路径以包含 /v0 前缀
  const navItems: NavItem[] = [
    { icon: FiHome, labelKey: 'v0/common:tasks', path: '/v0/tasks', tab: 'tasks' },
    { icon: FiFolder, labelKey: 'v0/common:projects', path: '/v0/projects', tab: 'projects' },
    { icon: FiCalendar, labelKey: 'v0/common:calendar', path: '/v0/calendar', tab: 'calendar' },
    { icon: FiMessageSquare, labelKey: 'v0/common:chat', path: '/v0/chat', tab: 'chat' },
    { icon: FiUser, labelKey: 'v0/common:meNav', path: '/v0/me', tab: 'me' },
  ];

  // 安全的导航函数 - 现在使用包含 /v0 的路径
  const safeNavigate = useCallback((path: string, tab: TabType) => {
    // 防止重复导航到当前路径
    if (location.pathname === path) return;
    
    // 设置过渡状态
    setNavTransitioning(true);

    // 使用startTransition包裹状态更新和导航操作
    startTransition(() => {
      setActiveTab(tab);
      navigate(path);
      
      // 延迟一段时间后重置过渡状态，允许下一次导航
      setTimeout(() => {
        setNavTransitioning(false);
      }, 500); // 500ms 防抖
    });
  }, [navigate, setActiveTab, location.pathname]);

  // 根据当前路径同步更新activeTab - startsWith 应该仍然有效
  useEffect(() => {
    const currentNavItem = navItems.find(item => location.pathname.startsWith(item.path));
    if (currentNavItem && currentNavItem.tab !== activeTab) {
      startTransition(() => {
        setActiveTab(currentNavItem.tab);
      });
    }
    // No changes needed here as startsWith should handle /v0/tasks starting with /v0/tasks
    // But ensure navItems is included in dependency array if it wasn't before (it wasn't explicitly shown)
  }, [location.pathname, setActiveTab, activeTab, navItems]); // Added navItems dependency

  // 渲染图标的辅助函数
  const renderIcon = (Icon: IconType | React.FC) => {
    // 如果是React图标库的图标
    if ('displayName' in Icon || (Icon as any).$$typeof) {
      const ReactIcon = Icon as IconType;
      return <ReactIcon className="w-6 h-6" />;
    }
    // 如果是自定义组件
    const CustomIcon = Icon as React.FC;
    return <CustomIcon />;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-surface-light dark:bg-surface-dark border-t border-border-light dark:border-border-dark py-2 px-4 z-10 h-14">
      <div className="flex justify-around items-center h-full">
        {navItems.map((item) => {
          const isActive = activeTab === item.tab;
          
          const activeColor = isDarkMode ? 'text-airbnb-teal' : 'text-airbnb-red';
          
          return (
            <div 
              key={item.path}
              onClick={() => !navTransitioning && safeNavigate(item.path, item.tab)}
              className="cursor-pointer"
            >
              <div
                className={`flex flex-col items-center ${
                  isActive ? activeColor : 'text-airbnb-gray dark:text-gray-400'
                }`}
              >
                {renderIcon(item.icon)}
                <span className="text-xs mt-1">
                  {t(item.labelKey)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}; 